import { useState, useEffect, useCallback } from 'react'
import { getRuns, getRunLogs, RunSummary, RunLogEntry } from '../api'
import { Button } from '@/components/ui/button'
import { Download } from 'lucide-react'

// Idea #54: машинный код причины завершения → русская строка для
// тултипа кнопки скачивания в истории. Дубль из server.py
// STOP_REASON_LABELS_RU, держим оба места в синхронизации.
const STOP_REASON_LABELS: Record<string, string> = {
  tasks_completed: 'Все задания выполнены',
  traffic_limit: 'Достигнут лимит трафика',
  time_limit: 'Закончилось время по расписанию',
  outside_work_hours: 'Вне рабочих часов по расписанию',
  manual_stop: 'Остановлен вручную',
  scheduler_day_off: 'Сегодня не рабочий день по расписанию',
  workers_died: 'Все воркеры упали (ошибки прокси/профилей)',
  panel_shutdown: 'Перезагрузка сервера / панели',
  max_restarts: 'Достигнут лимит перезапусков',
  stale: 'Закрыт автоматически',
}

function reasonLabel(code?: string | null): string {
  if (!code) return ''
  return STOP_REASON_LABELS[code] || code
}

function downloadRunUrl(mode: string, cid: string, runId: string): string {
  return `/api/${mode}/campaigns/${encodeURIComponent(cid)}/logs/download?run_id=${encodeURIComponent(runId)}`
}

function formatDt(ts: number): string {
  const d = new Date(ts * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function formatDuration(start: number, end: number | null): string {
  if (!end) return 'работает...'
  const s = end - start
  if (s < 60) return `${s}с`
  if (s < 3600) return `${Math.floor(s / 60)}м ${s % 60}с`
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return `${h}ч ${m}м`
}

function formatLogTs(ts: number): string {
  const d = new Date(ts * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function levelColor(level: string): string {
  switch (level) {
    case 'ERROR':
    case 'CRITICAL':
      return 'text-red-400'
    case 'WARNING':
      return 'text-amber-400'
    default:
      return 'text-muted-foreground'
  }
}

function RunLogViewer({ runId, downloadHref }: { runId: string; downloadHref?: string }) {
  const [logs, setLogs] = useState<RunLogEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [offset, setOffset] = useState(0)
  const PAGE = 500

  const load = useCallback(async (off: number) => {
    setLoading(true)
    try {
      const res = await getRunLogs(runId, PAGE, off)
      setLogs(res.logs)
      setTotal(res.total)
      setOffset(off)
    } catch {}
    setLoading(false)
  }, [runId])

  useEffect(() => { load(0) }, [load])

  const pages = Math.ceil(total / PAGE)
  const currentPage = Math.floor(offset / PAGE)

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 flex items-center gap-3 px-3 py-1.5 border-b border-border">
        <span className="text-xs tabular-nums text-muted-foreground">
          {total} записей
        </span>
        <div className="flex items-center gap-1 ml-auto">
          {pages > 1 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => load(Math.max(0, offset - PAGE))}
                disabled={offset === 0}
                className="h-6 px-2 text-xs"
              >{'\u2190'}</Button>
              <span className="text-xs tabular-nums px-1 text-muted-foreground">
                {currentPage + 1}/{pages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => load(Math.min((pages - 1) * PAGE, offset + PAGE))}
                disabled={offset + PAGE >= total}
                className="h-6 px-2 text-xs"
              >{'\u2192'}</Button>
            </>
          )}
          {downloadHref && (
            <a
              href={downloadHref}
              className="inline-flex items-center gap-1 h-6 px-2 text-xs rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              title="Скачать полный лог в .txt"
            >
              <Download className="w-3.5 h-3.5" /> Скачать
            </a>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto font-mono text-xs leading-[1.6]">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">Загрузка...</div>
        ) : logs.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">Нет записей</div>
        ) : (
          <div className="px-3 py-1">
            {logs.map((entry, i) => (
              <div key={i} className="flex gap-2 py-px hover:bg-accent/50">
                <span className="shrink-0 tabular-nums text-muted-foreground" style={{ width: 60 }}>
                  {formatLogTs(entry.ts)}
                </span>
                <span className={levelColor(entry.level)}>
                  {entry.message}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface RunHistoryProps {
  campaignId?: string
  mode?: 'booster' | 'profiler'
}

export default function RunHistory({ campaignId, mode = 'booster' }: RunHistoryProps) {
  const [runs, setRuns] = useState<RunSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedRun, setSelectedRun] = useState<string | null>(null)

  const loadRuns = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getRuns(10, 0, campaignId)
      setRuns(data)
    } catch {}
    setLoading(false)
  }, [campaignId])

  useEffect(() => { loadRuns() }, [loadRuns])

  const hrefFor = useCallback(
    (rid: string) => (campaignId ? downloadRunUrl(mode, campaignId, rid) : ''),
    [campaignId, mode],
  )

  if (selectedRun) {
    const run = runs.find(r => r.id === selectedRun)
    const dlHref = hrefFor(selectedRun)
    return (
      <div className="flex flex-col h-full">
        <div className="shrink-0 flex items-center gap-3 px-3 py-1.5 border-b border-border">
          <Button variant="ghost" size="sm" onClick={() => setSelectedRun(null)} className="h-7 text-xs gap-1.5">
            {'\u2190'} Назад
          </Button>
          {run && (
            <span className="text-xs font-medium text-foreground">
              {run.campaign_name || run.mode} {'\u2014'} {formatDt(run.started_at)}
            </span>
          )}
          {dlHref && (
            <a
              href={dlHref}
              className="ml-auto inline-flex items-center gap-1 h-7 px-2 text-xs rounded-md border border-border hover:bg-accent transition"
              title="Скачать полный лог запуска в .txt"
            >
              <Download className="w-3.5 h-3.5" /> Скачать лог
            </a>
          )}
          <Button variant="ghost" size="sm" onClick={loadRuns} className={dlHref ? 'h-7 text-xs' : 'ml-auto h-7 text-xs'}>
            {'\u21BB'}
          </Button>
        </div>
        <div className="flex-1 min-h-0">
          <RunLogViewer runId={selectedRun} downloadHref={dlHref || undefined} />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 flex items-center gap-3 px-3 py-1.5 border-b border-border">
        <span className="text-xs font-medium text-foreground">
          История запусков
        </span>
        <Button variant="ghost" size="sm" onClick={loadRuns} className="ml-auto h-7 text-xs gap-1">
          {'\u21BB'} Обновить
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">Загрузка...</div>
        ) : runs.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
            Нет сохранённых запусков
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                {['Проект', 'Начало', 'Длительность', 'Успешно', 'Ошибки', 'Капча', 'Причина', ''].map(h => (
                  <th key={h} className="text-left px-3 py-1.5 font-medium text-muted-foreground">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {runs.map(run => {
                const stopReason = (run as { stop_reason?: string | null }).stop_reason
                const reasonText = reasonLabel(stopReason)
                const href = hrefFor(run.id)
                return (
                  <tr
                    key={run.id}
                    className="cursor-pointer transition-colors hover:bg-accent/50 border-b border-border"
                    onClick={() => setSelectedRun(run.id)}
                  >
                    <td className="px-3 py-1.5 font-medium text-foreground">
                      <div className="flex items-center gap-2">
                        {!run.stopped_at && (
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                        )}
                        {run.campaign_name || run.mode}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{formatDt(run.started_at)}</td>
                    <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{formatDuration(run.started_at, run.stopped_at)}</td>
                    <td className={`px-3 py-1.5 tabular-nums ${run.success > 0 ? 'text-green-500' : 'text-muted-foreground'}`}>{run.success}</td>
                    <td className={`px-3 py-1.5 tabular-nums ${run.fail > 0 ? 'text-red-500' : 'text-muted-foreground'}`}>{run.fail}</td>
                    <td className={`px-3 py-1.5 tabular-nums ${run.captcha > 0 ? 'text-yellow-500' : 'text-muted-foreground'}`}>{run.captcha}</td>
                    <td className="px-3 py-1.5 text-muted-foreground truncate max-w-[18ch]" title={reasonText || ''}>
                      {reasonText || (run.stopped_at ? '\u2014' : '')}
                    </td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      {href && (
                        <a
                          href={href}
                          onClick={e => e.stopPropagation()}
                          className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                          title="Скачать полный лог запуска в .txt"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      )}
                      <span className="text-xs text-muted-foreground ml-1">{'\u25B6'}</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
